<template>
  <!-- Post-trip bus walkthroughs still open (utils/busWalkthrough.ts `shownChecks`): on every screen, with the time left.
       The one open in the modal is hidden here. Mandatory, so it is loud: solid primary, solid red for the last
       URGENT_MS. -->
  <div
    v-for="check in visible"
    :key="check.tripID"
    class="pointer-events-auto flex items-center gap-3 w-full max-w-lg rounded-2xl shadow-[0_8px_24px_-8px_rgb(0_0_0/0.45)] py-3 pl-4 pr-2 text-base"
    :class="tone(check)"
    @click="open(check)"
  >
    <vs-icon :name="check.lost ? 'lucide:shield-off' : 'lucide:shield-alert'" :size="24" class="flex-shrink-0" />
    <div class="flex-1 min-w-0">
      <template v-if="check.lost">
        <div class="font-semibold">Revisión del bus cerrada</div>
        <div class="opacity-80">Saliste de la zona del bus. Quedó sin hacer.</div>
      </template>
      <template v-else-if="check.queued">
        <div class="font-semibold">Revisión guardada sin conexión</div>
        <div class="opacity-80">Se envía cuando tengas señal.</div>
      </template>
      <template v-else>
        <div class="font-semibold">Revisión del bus pendiente</div>
        <div class="text-sm opacity-90 truncate">{{ [check.routeName, check.vehicleName].filter(Boolean).join(' · ') || 'Escanea el código del último puesto' }}</div>
      </template>
    </div>
    <template v-if="!check.lost && !check.queued">
      <span class="text-lg font-semibold tabular-nums" aria-hidden="true">{{ countdown(check) }}</span>
      <vs-button
        class="!py-2 !px-3"
        :kind="urgent(check) ? 'error' : 'primary'"
        inverted
        iconName="lucide:scan-line"
        text="Escanear"
        :aria-label="`Escanear el código. Quedan ${countdown(check)}`"
        @click.stop="open(check)"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { vsButton, vsIcon } from '@isagaco/vuesaga'
import { activeBusWalkthrough, nowMs, openBusWalkthrough, shownChecks, URGENT_MS, type ShownCheck } from '@/utils/busWalkthrough'

export default defineComponent({
  name: 'BusWalkthroughBanner',
  components: { vsButton, vsIcon },
  setup() {
    const visible = computed(() => shownChecks.value.filter(check => check.tripID !== activeBusWalkthrough.value?.tripID))
    const countdown = (check: ShownCheck) => {
      const total = Math.max(0, Math.ceil((Date.parse(check.dueAt) - nowMs.value) / 1000))
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
    }
    const urgent = (check: ShownCheck) => Date.parse(check.dueAt) - nowMs.value <= URGENT_MS
    const tone = (check: ShownCheck) => {
      if (check.lost || check.queued) return 'bg-surface-variant text-on-surface-variant'
      return urgent(check) ? 'bg-error text-on-error' : 'bg-primary text-on-primary'
    }
    const open = (check: ShownCheck) => {
      if (check.lost || check.queued) return
      void openBusWalkthrough({ tripID: check.tripID, dueAt: check.dueAt, routeName: check.routeName, vehicleName: check.vehicleName })
    }
    return { visible, countdown, urgent, tone, open }
  },
})
</script>
